import { useEffect } from 'react'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { getPeriodOptions, formatPeriodDisplay } from '@/utils/periodHelpers'
import { usePeriodSelection } from '@/hooks/usePeriodSelection'

interface SmartPeriodSelectorProps {
  value: string
  onChange: (period: string) => void
  storageKey: string
  availablePeriods?: { value: string; label: string }[]
  required?: boolean
  className?: string
}

/**
 * Smart period selector with intelligent default selection
 *
 * Features:
 * - Remembers last selected period
 * - Auto-advances to previous month when month rolls over (payroll lag pattern)
 * - Uses shadcn/ui Select component
 * - Auto-initializes parent with suggested period if value is empty
 *
 * @param value - Currently selected period
 * @param onChange - Callback when period changes
 * @param storageKey - Unique localStorage key for this context
 * @param availablePeriods - Optional array of period options. If not provided, generates 12 months back + 1 forward
 * @param required - Whether to show required indicator
 * @param className - Optional CSS classes
 */
export function SmartPeriodSelector({
  value,
  onChange,
  storageKey,
  availablePeriods,
  required = false,
  className = ''
}: SmartPeriodSelectorProps) {
  // Generate period options if not provided
  const periodOptions = availablePeriods || getPeriodOptions()
  const periodValues = periodOptions.map(p => p.value)

  // Get smart suggestion based on history and current date
  const { suggestedPeriod, savePeriodSelection } = usePeriodSelection(storageKey, periodValues)

  // Auto-initialize with suggested period if parent value is empty
  useEffect(() => {
    if (!value && suggestedPeriod) {
      onChange(suggestedPeriod)
      savePeriodSelection(suggestedPeriod)
    }
  }, [value, suggestedPeriod, onChange, savePeriodSelection])

  const handleChange = (selectedPeriod: string) => {
    onChange(selectedPeriod)
    savePeriodSelection(selectedPeriod)
  }

  return (
    <div className="space-y-2">
      <Select value={value} onValueChange={handleChange}>
        <SelectTrigger className={className || 'w-full'}>
          <SelectValue placeholder="Välj period" />
        </SelectTrigger>
        <SelectContent>
          {periodOptions.map(period => (
            <SelectItem key={period.value} value={period.value}>
              {formatPeriodDisplay(period.value)}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
    </div>
  )
}
